import { useState } from 'react';

import type { ICheckboxesDropdownMenu } from '../../../../types/Dropdown/dropdown_menu/dropdown-menu';
import type {
  ISelectFilterDropdown,
  TTextValuePair,
} from '../../../../types/data_table';

import { FormatCurrencyForDisplay } from '../../../../utils/FormatCurrencyForDisplay';
import { removeArrDuplicates } from '../../../../utils/removeArrDuplicates';

import { Dropdown } from '../../../Dropdown';
import { DropdownButton } from '../../../Dropdown/DropdownButton';
import { DropdownMenu } from '../../../Dropdown/DropdownMenu';
import { DropdownMenuItem } from '../../../Dropdown/DropdownMenuItem';

/**
 * One dropdown filter, single or multi selection.
 *
 * Single selection reports every click straight up. Multi selection is
 * deliberately staged: ticking boxes only edits this component's own pending
 * set, and nothing reaches the table until the user hits Apply — closing the
 * menu instead throws the pending set away and restores what is applied.
 */
export const SelectFilterDropdown = ({
  filter,
  lang,
  disabled,
  localization,
  onChange,
}: ISelectFilterDropdown) => {
  const {
    id,
    type,
    label,
    options,
    defaultOption,
    selectedOptions,
    selectMultiple,
    multipleSelectionSummaryType,
    multiSelectionParamType,
    maxHeight,
    countList,
  } = filter;

  /** Boxes ticked in the open menu, not yet applied to the table. */
  const [pendingOptions, setPendingOptions] = useState<
    TTextValuePair[] | undefined
  >(options);

  const pendingCount = pendingOptions?.length || 0;
  const isEverythingPending = pendingCount === options?.length;

  /** Total across the whole filter, shown on the select-all row. */
  const totalCount = (countList || []).reduce(
    (sum: number, count) => (typeof count === 'number' ? sum + count : sum),
    0
  );

  /** Summarises the applied selection on the dropdown's button. */
  const getButtonText = () => {
    if (!Array.isArray(selectedOptions)) {
      return FormatCurrencyForDisplay(
        typeof selectedOptions === 'string'
          ? selectedOptions
          : selectedOptions?.text || defaultOption
      ) as any;
    }

    if (selectedOptions.length === options?.length) return localization?.all;

    if (selectedOptions.length === 0) return localization?.none;

    const firstOptionText = FormatCurrencyForDisplay(
      selectedOptions[0].text
    ) as any;

    if (selectedOptions.length === 1) return firstOptionText;

    return (
      <>
        {firstOptionText}
        {`, +${selectedOptions.length - 1}`}
      </>
    );
  };

  /**
   * Selecting everything is the same as selecting nothing, so the clear
   * action only appears once the filter is actually narrowing the results.
   */
  const isNarrowing = selectMultiple
    ? Array.isArray(selectedOptions) &&
      selectedOptions.length !== options.length
    : !!selectedOptions;

  /** Restores the filter to "no narrowing applied". */
  const handleClear = () => {
    onChange(id, type, selectMultiple ? options : (null as any));
    setPendingOptions(selectMultiple ? options : undefined);
  };

  const togglePendingOption = (option: TTextValuePair, checked: boolean) =>
    setPendingOptions((previous) =>
      checked
        ? removeArrDuplicates([...(previous || []), option], 'value')
        : previous?.filter((pending) => pending.value !== option.value)
    );

  /** Props that only a multi-selection menu takes. */
  const checkboxesMenuProps = selectMultiple
    ? ({
        lang,
        onApplyHandler: () =>
          onChange(id, type, pendingOptions || [], multiSelectionParamType),
        applyBtnText: localization?.apply,
        applyBtnDisabled: !pendingCount,
      } as ICheckboxesDropdownMenu)
    : {};

  return (
    <Dropdown width="fit-content">
      <DropdownButton
        label={label}
        text={getButtonText()}
        disabled={disabled}
        onClearHandler={isNarrowing ? handleClear : undefined}
      />
      <DropdownMenu
        width="fit-content"
        horizontalPlacement="auto"
        type={selectMultiple ? 'checkboxes' : 'default'}
        {...(maxHeight ? { maxHeight } : {})}
        // Closing without applying discards the pending ticks.
        onToggleHandler={(open) => {
          if (open === false && selectMultiple) {
            setPendingOptions(selectedOptions as TTextValuePair[] | undefined);
          }
        }}
        {...checkboxesMenuProps}
      >
        {selectMultiple && (
          <DropdownMenuItem
            key={`${id}-select-all-option`}
            type="checkbox"
            checkboxIcon="dash"
            id={`${id}-select-all`}
            text={
              multipleSelectionSummaryType === 'all' ?
                localization?.all
                :
                `${pendingCount} ${localization?.selected}`
            }
            count={countList && totalCount}
            checked={isEverythingPending}
            onCheckboxItemClickHandler={(checked) =>
              setPendingOptions(checked ? options : undefined)
            }
            closeOnItemClick={false}
          />
        )}

        {options?.map((option, index) => {
          const optionText = FormatCurrencyForDisplay(option.text) as any;

          return selectMultiple ? (
            <DropdownMenuItem
              key={`${id}-option-${index}`}
              type="checkbox"
              id={`${id}-option-${index}`}
              text={optionText}
              count={countList?.[index]}
              checked={
                !!pendingOptions?.some(
                  (pending) => pending.value === option.value
                )
              }
              onCheckboxItemClickHandler={(checked) =>
                togglePendingOption(option, checked === true)
              }
              closeOnItemClick={false}
            />
          ) : (
            <DropdownMenuItem
              key={`${id}-option-${index}`}
              type="default"
              text={optionText}
              count={countList?.[index]}
              onClickHandler={() => onChange(id, type, option)}
              selected={
                (selectedOptions as TTextValuePair)?.value === option.value
              }
            />
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
};
